import React from 'react';
import {motion} from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import {Link} from 'react-router-dom';

const {FiPlay, FiExternalLink, FiShield, FiEye, FiAlertTriangle} = FiIcons;

const VideoGallery = () => {
  return (
    <section className="py-20 bg-gray-800">
      <div className="container mx-auto px-6">
        <motion.div 
          className="text-center mb-12" 
          initial={{ opacity: 0, y: 30 }} 
          whileInView={{ opacity: 1, y: 0 }} 
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-pink-500 to-cyan-400 bg-clip-text text-transparent">
            Exposing Scammers, Educating Millions
          </h2>
          <p className="text-xl text-gray-300 max-w-4xl mx-auto">
            Linda Roberts has dedicated her platform to turning the tables on internet fraudsters while 
            teaching millions how to protect themselves from increasingly sophisticated online scams.
          </p>
        </motion.div>

        <div className="max-w-5xl mx-auto">
          <motion.div 
            className="bg-gray-900 p-8 md:p-12 rounded-2xl border border-purple-500/30 mb-12"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            viewport={{ once: true }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                  The LRCS Mission
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6">
                  In an era where cybercrime costs Americans billions annually, Linda Roberts Calls Scammers 
                  serves as both shield and sword—protecting potential victims while exposing fraudulent operations.
                </p>
                <p className="text-gray-300 leading-relaxed">
                  Through authentic scam baiting content, LRCS has helped millions identify common scam tactics
                  and avoid becoming victims themselves.
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-4 bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <SafeIcon icon={FiAlertTriangle} className="text-2xl text-pink-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-white mb-1">Romance Scam Red Flags</h4>
                    <p className="text-gray-400 text-sm">Learning to identify suspicious behavior in online relationships</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <SafeIcon icon={FiAlertTriangle} className="text-2xl text-purple-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-white mb-1">Tech Support Fraud</h4>
                    <p className="text-gray-400 text-sm">Understanding how fake tech support scammers operate</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-4 bg-gray-800/50 p-4 rounded-xl border border-gray-700">
                  <SafeIcon icon={FiAlertTriangle} className="text-2xl text-cyan-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-bold text-white mb-1">Government Impersonation</h4>
                    <p className="text-gray-400 text-sm">Spotting fake IRS, Social Security, and other government scams</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            className="bg-gray-900 p-8 md:p-12 rounded-2xl border border-pink-500/30"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-white mb-6 text-center">
              Making a Difference
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 flex flex-col items-center text-center">
                <div className="text-4xl font-bold text-pink-500 mb-2">3K+</div>
                <h4 className="font-bold text-white mb-2">YouTube Subscribers</h4>
                <p className="text-gray-400 text-sm">Growing community of vigilant viewers</p>
              </div>
              
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 flex flex-col items-center text-center">
                <div className="text-4xl font-bold text-purple-500 mb-2">15K+</div>
                <h4 className="font-bold text-white mb-2">Scams Exposed</h4>
                <p className="text-gray-400 text-sm">Fraudulent operations identified and reported</p>
              </div>
              
              <div className="bg-gray-800/50 p-6 rounded-xl border border-gray-700 flex flex-col items-center text-center">
                <div className="text-4xl font-bold text-cyan-500 mb-2">100M+</div>
                <h4 className="font-bold text-white mb-2">Video Views</h4>
                <p className="text-gray-400 text-sm">Educational content reaching a global audience</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="http://link.lrcs.nyc/YTSL" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-3 shadow-lg justify-center"
              >
                <SafeIcon icon={FiPlay} className="text-xl" />
                Subscribe on YouTube
              </a>
              
              <Link 
                to="/scam-assistance"
                className="bg-transparent border-2 border-cyan-400 hover:bg-cyan-400 hover:text-gray-900 text-cyan-400 px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-105 flex items-center gap-3 justify-center"
              >
                <SafeIcon icon={FiShield} className="text-xl" />
                Get Scam Help
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default VideoGallery;